import { useState } from 'react';
import { Loader2, Plus, Trash2, ArrowLeft } from 'lucide-react';
import { Button } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Textarea } from '@travellegacy/nexops-design-system';
import { Select } from '@travellegacy/nexops-design-system';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { QuarterSelector } from './QuarterSelector';
import { useCreatePDI } from '../hooks/usePDIs';
import { useEmployees } from '@/features/people-core/hooks/useEmployees';
import type { CreatePDIInput } from '../types';

interface CreatePDIFormProps {
  onCancel: () => void;
  onSuccess?: () => void;
}

interface ObjectiveForm {
  title: string;
  action: string;
  successIndicator: string;
  deadline: string;
  supportNeeded: string;
}

const emptyObjective: ObjectiveForm = {
  title: '',
  action: '',
  successIndicator: '',
  deadline: '',
  supportNeeded: '',
};

/**
 * Formulário para criar novo PDI
 */
export function CreatePDIForm({ onCancel, onSuccess }: CreatePDIFormProps) {
  const now = new Date();
  const currentQuarter = Math.ceil((now.getMonth() + 1) / 3) as 1 | 2 | 3 | 4;

  const [employeeId, setEmployeeId] = useState('');
  const [quarter, setQuarter] = useState<1 | 2 | 3 | 4>(currentQuarter);
  const [year, setYear] = useState(now.getFullYear());
  const [objectives, setObjectives] = useState<ObjectiveForm[]>([{ ...emptyObjective }]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const createPDI = useCreatePDI();
  const { data: employees } = useEmployees();

  const handleAddObjective = () => {
    setObjectives([...objectives, { ...emptyObjective }]);
  };

  const handleRemoveObjective = (index: number) => {
    if (objectives.length === 1) return;
    setObjectives(objectives.filter((_, i) => i !== index));
  };

  const handleObjectiveChange = (index: number, field: keyof ObjectiveForm, value: string) => {
    const updated = [...objectives];
    updated[index] = { ...updated[index], [field]: value };
    setObjectives(updated);

    if (errors[`objective_${index}_${field}`]) {
      const newErrors = { ...errors };
      delete newErrors[`objective_${index}_${field}`];
      setErrors(newErrors);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!employeeId) {
      newErrors.employeeId = 'Selecione um colaborador';
    }

    objectives.forEach((obj, index) => {
      if (!obj.title.trim()) {
        newErrors[`objective_${index}_title`] = 'Título é obrigatório';
      }
      if (!obj.action.trim()) {
        newErrors[`objective_${index}_action`] = 'Ação é obrigatória';
      }
      if (!obj.successIndicator.trim()) {
        newErrors[`objective_${index}_successIndicator`] = 'Indicador é obrigatório';
      }
      if (!obj.deadline) {
        newErrors[`objective_${index}_deadline`] = 'Prazo é obrigatório';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const data: CreatePDIInput = {
      employeeId,
      quarter,
      year,
      objectives: objectives.map((obj) => ({
        title: obj.title,
        action: obj.action,
        successIndicator: obj.successIndicator,
        deadline: obj.deadline,
        supportNeeded: obj.supportNeeded || undefined,
      })),
    };

    await createPDI.mutateAsync(data);
    onSuccess?.();
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={onCancel}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
        <div>
          <h2 className="text-2xl font-bold">Criar Novo PDI</h2>
          <p className="text-muted-foreground">Plano de Desenvolvimento Individual trimestral</p>
        </div>
      </div>

      {/* Informações Básicas */}
      <Card>
        <CardHeader>
          <CardTitle>Informações Básicas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Colaborador */}
          <Select
            label="Colaborador"
            value={employeeId}
            onChange={(e) => {
              setEmployeeId(e.target.value);
              if (errors.employeeId) {
                const newErrors = { ...errors };
                delete newErrors.employeeId;
                setErrors(newErrors);
              }
            }}
            error={errors.employeeId}
            options={[
              { value: '', label: 'Selecione um colaborador' },
              ...(employees?.map((emp) => ({
                value: emp.id,
                label: emp.user?.name || 'Sem nome',
              })) || []),
            ]}
          />

          {/* Trimestre e Ano */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <QuarterSelector
              label="Trimestre"
              value={quarter}
              onChange={(q) => setQuarter(q as 1 | 2 | 3 | 4)}
            />

            <Select
              label="Ano"
              value={year.toString()}
              onChange={(e) => setYear(parseInt(e.target.value))}
              options={[2024, 2025, 2026, 2027].map((y) => ({
                value: y.toString(),
                label: y.toString(),
              }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Objetivos */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Objetivos de Desenvolvimento</CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAddObjective}
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Objetivo
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {objectives.map((objective, index) => (
            <div
              key={index}
              className="border border-border rounded-lg p-4 space-y-4 relative bg-muted/30"
            >
              {/* Botão remover */}
              {objectives.length > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="absolute top-2 right-2"
                  onClick={() => handleRemoveObjective(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}

              <h4 className="text-sm font-semibold text-muted-foreground">
                Objetivo {index + 1}
              </h4>

              <Input
                label="Título do Objetivo"
                value={objective.title}
                onChange={(e) => handleObjectiveChange(index, 'title', e.target.value)}
                error={errors[`objective_${index}_title`]}
                placeholder="Ex: Aprender TypeScript avançado"
              />

              <Textarea
                label="Ação / Como alcançar"
                value={objective.action}
                onChange={(e) => handleObjectiveChange(index, 'action', e.target.value)}
                error={errors[`objective_${index}_action`]}
                placeholder="Descreva as ações concretas para alcançar este objetivo..."
                rows={3}
              />

              <Textarea
                label="Indicador de Sucesso"
                value={objective.successIndicator}
                onChange={(e) =>
                  handleObjectiveChange(index, 'successIndicator', e.target.value)
                }
                error={errors[`objective_${index}_successIndicator`]}
                placeholder="Como saberemos que o objetivo foi alcançado?"
                rows={2}
              />

              <Input
                label="Prazo"
                type="date"
                value={objective.deadline}
                onChange={(e) => handleObjectiveChange(index, 'deadline', e.target.value)}
                error={errors[`objective_${index}_deadline`]}
              />

              <Textarea
                label="Apoio Necessário (opcional)"
                value={objective.supportNeeded}
                onChange={(e) =>
                  handleObjectiveChange(index, 'supportNeeded', e.target.value)
                }
                placeholder="Recursos, treinamentos ou suporte necessário do gestor..."
                rows={2}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Ações */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancelar
            </Button>
            <Button onClick={handleSubmit} disabled={createPDI.isPending}>
              {createPDI.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Criando...
                </>
              ) : (
                'Criar PDI'
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
